import {useState, useRef, useEffect} from "react";
import {useTranslation} from "react-i18next";
import type {TFunction} from "i18next";
import {isToday, subDays} from "date-fns";
import {Text, UnstyledButton, TextInput, ActionIcon} from "@mantine/core";
import {LinkRow} from "../LinkRow/LinkRow";
import IconSearch from "../../assets/search.svg?react";
import classes from "./DashboardLinkList.module.css";

// Entrance: one running counter per column, like the front page domino.
// Each column starts COLUMN_STAGGER_MS after the one to its left. Inside a
// column the box, title, description, search icon and rows each take the
// next STEP_MS slot. The counter is capped so the tail stays short.
const COLUMN_STAGGER_MS = 90;
const STEP_MS = 30;
const MAX_STEPS = 12;
const HEADER_STEPS = 4;
const ENTRANCE_WINDOW_MS = 1500;

// The entrance plays once per page load. Tab re-entry remounts the columns
// with cached data, and must not replay it.
let entrancePlayed = false;

export type LinkListItem = {
    name: string;
    description?: string;
    date?: string;
    sectionLabel?: string;
    rightSection?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
} & (
    | {href: string; download?: string; to?: never}
    | {to: string; href?: never; download?: never}
);

export type DashboardLinkListVariant = "flat" | "grouped" | "pinned-grouped";

type DashboardLinkListProps = {
    title: string;
    description?: string;
    items: LinkListItem[];
    variant?: DashboardLinkListVariant;
    maxItems?: number;
    viewAllHref?: string;
    background?: string;
    emptyText?: string;
    loading?: boolean;
    animationIndex?: number;
};

type Group = {
    label: string;
    items: LinkListItem[];
    kind: "section" | "date";
};

function groupByDate(items: LinkListItem[], t: TFunction, showPreviousLabel = false): Group[] {
    const sevenDaysAgo = subDays(new Date(), 7);
    const today: LinkListItem[] = [];
    const lastWeek: LinkListItem[] = [];
    const previous: LinkListItem[] = [];

    for (const item of items) {
        const date = item.date ? new Date(item.date) : null;
        if (date && isToday(date)) {
            today.push(item);
        } else if (date && date >= sevenDaysAgo) {
            lastWeek.push(item);
        } else {
            previous.push(item);
        }
    }

    const hasRecentGroup = today.length > 0 || lastWeek.length > 0;
    const result: Group[] = [];
    if (today.length > 0)
        result.push({label: t("date.today"), items: today, kind: "date"});
    if (lastWeek.length > 0)
        result.push({
            label: t("date.lastWeek"),
            items: lastWeek,
            kind: "date",
        });
    if (previous.length > 0)
        result.push({
            label: hasRecentGroup || showPreviousLabel ? t("date.previously") : "",
            items: previous,
            kind: "date",
        });
    return result;
}

function groupBySectionLabel(items: LinkListItem[]): Group[] {
    if (!items.some((i) => i.sectionLabel)) {
        return [{label: "", items, kind: "section"}];
    }
    const groups: Group[] = [];
    for (const item of items) {
        const label = item.sectionLabel ?? "";
        const last = groups[groups.length - 1];
        if (last && last.label === label) {
            last.items.push(item);
        } else {
            groups.push({label, items: [item], kind: "section"});
        }
    }
    return groups;
}

/**
 * Pinned items (those carrying a sectionLabel) stay flat at the top; the rest
 * (e.g. last-visited history, which carry a `date`) are date-grouped below —
 * matching the threads column's today / last-week / earlier buckets.
 */
function groupPinnedThenDate(items: LinkListItem[], t: TFunction): Group[] {
    const pinned = items.filter((i) => i.sectionLabel);
    const rest = items.filter((i) => !i.sectionLabel);
    const groups: Group[] = [];
    if (pinned.length > 0) groups.push(...groupBySectionLabel(pinned));
    if (rest.length > 0) groups.push(...groupByDate(rest, t, pinned.length > 0));
    return groups;
}

function filterItems(items: LinkListItem[], query: string): LinkListItem[] {
    if (!query) return items;
    let regex: RegExp;
    try {
        regex = new RegExp(query, "i");
    } catch {
        regex = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
    }
    return items.filter(
        (item) => regex.test(item.name) || (item.description && regex.test(item.description))
    );
}

export function DashboardLinkList({
    title,
    description,
    items,
    variant = "flat",
    maxItems = 5,
    viewAllHref,
    background,
    emptyText,
    loading = false,
    animationIndex = 0,
}: DashboardLinkListProps) {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [query, setQuery] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (searchOpen) inputRef.current?.focus();
    }, [searchOpen]);

    // The box and header animate in on mount. The rows animate in when the
    // data arrives. Both sets of classes drop after their window so later
    // re-renders (search, expand) do not replay.
    const mountTime = useRef(performance.now());
    const shouldAnimate = useRef(!entrancePlayed).current;
    const columnDelay = animationIndex * COLUMN_STAGGER_MS;
    const [shellDone, setShellDone] = useState(false);
    const [rowsDone, setRowsDone] = useState(false);
    const shellAnimate = shouldAnimate && !shellDone;
    const rowsAnimate = shouldAnimate && !loading && !rowsDone;

    useEffect(() => {
        entrancePlayed = true;
    }, []);

    useEffect(() => {
        const id = setTimeout(() => setShellDone(true), columnDelay + ENTRANCE_WINDOW_MS);
        return () => clearTimeout(id);
    }, [columnDelay]);

    useEffect(() => {
        if (!rowsAnimate) return;
        const id = setTimeout(() => setRowsDone(true), columnDelay + ENTRANCE_WINDOW_MS);
        return () => clearTimeout(id);
    }, [rowsAnimate, columnDelay]);

    const stepDelay = (step: number) => columnDelay + Math.min(step, MAX_STEPS) * STEP_MS;
    const shellClass = shellAnimate ? classes.itemAnimate : "";
    const shellStyle = (step: number) =>
        shellAnimate ? {animationDelay: `${stepDelay(step)}ms`} : undefined;

    // Rows continue the counter when the data arrives early. When the data
    // arrives late, the rows start at once.
    const elapsed = performance.now() - mountTime.current;
    const rowsBase = Math.max(0, columnDelay - elapsed);
    const rowsClass = rowsAnimate ? classes.itemAnimate : "";
    const rowStyle = (step: number) =>
        rowsAnimate
            ? {animationDelay: `${rowsBase + Math.min(step, MAX_STEPS) * STEP_MS}ms`}
            : undefined;

    if (!loading && items.length === 0 && !emptyText) return null;

    const sorted =
        variant === "grouped"
            ? [...items].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""))
            : items;

    const filtered = filterItems(sorted, query);
    const isSearching = query.length > 0;
    const hasMore = !isSearching && filtered.length > maxItems;
    const visible = isSearching || expanded ? filtered : filtered.slice(0, maxItems);

    const groups: Group[] =
        variant === "grouped"
            ? groupByDate(visible, t)
            : variant === "pinned-grouped"
              ? groupPinnedThenDate(visible, t)
              : groupBySectionLabel(visible);

    const handleCloseSearch = () => {
        setSearchOpen(false);
        setQuery("");
        setExpanded(false);
    };

    // Running index for the row stagger (top-to-bottom across groups).
    let rowIndex = HEADER_STEPS;

    return (
        <div
            className={`${classes.root} ${shellClass}`.trim()}
            style={{
                ...(background ? {backgroundColor: background} : {}),
                ...shellStyle(0),
            }}
        >
            <div className={classes.header}>
                <Text className={`${classes.title} ${shellClass}`.trim()} style={shellStyle(1)}>{title}</Text>
                <div className={`${classes.searchWrapper} ${shellClass}`.trim()} style={shellStyle(3)}>
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        aria-label={t("common.search")}
                        onClick={() => setSearchOpen(true)}
                        className={classes.searchIcon}
                        style={{visibility: searchOpen ? "hidden" : "visible", position: "absolute", right: 0}}
                    >
                        <IconSearch width={16} height={16} />
                    </ActionIcon>
                    <TextInput
                        ref={inputRef}
                        size="xs"
                        placeholder={`${t("common.search")}…`}
                        value={query}
                        onChange={(e) => {
                            setQuery(e.currentTarget.value);
                            setExpanded(false);
                        }}
                        onBlur={() => {
                            if (!query) handleCloseSearch();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") handleCloseSearch();
                        }}
                        style={{visibility: searchOpen ? "visible" : "hidden"}}
                        classNames={{root: classes.searchRoot, input: classes.searchInput}}
                    />
                </div>
            </div>
            {description && (
                <Text className={`${classes.description} ${shellClass}`.trim()} style={shellStyle(2)}>
                    {description}
                </Text>
            )}
            {loading && (
                <div className={classes.skeleton}>
                    <div className={`${classes.skeletonLabel} ${shellClass}`.trim()} style={shellStyle(HEADER_STEPS)}>
                        <div className={classes.placeholderBar} style={{height: 12, width: "20%"}} />
                    </div>
                    {Array.from({length: maxItems}, (_, i) => (
                        <div
                            key={i}
                            className={`${classes.skeletonRow} ${shellClass}`.trim()}
                            style={shellStyle(HEADER_STEPS + 1 + i)}
                        >
                            <div className={classes.placeholderBar} style={{height: 10, width: `${70 - (i % 3) * 15}%`}} />
                            <div className={classes.placeholderBar} style={{height: 8, width: "30%"}} />
                        </div>
                    ))}
                    <div
                        className={`${classes.skeletonExpand} ${shellClass}`.trim()}
                        style={shellStyle(HEADER_STEPS + 1 + maxItems)}
                    >
                        <div className={classes.placeholderBar} style={{height: 10, width: "25%"}} />
                    </div>
                </div>
            )}
            {!loading && items.length === 0 && emptyText && (
                <Text className={`${classes.emptyText} ${rowsClass}`.trim()} style={rowStyle(HEADER_STEPS)}>
                    {emptyText}
                </Text>
            )}
            {groups.map((group) => (
                <div key={group.label}>
                    {group.label && (
                        <Text
                            className={`${classes.sectionLabel} ${rowsClass}`.trim()}
                            style={rowStyle(rowIndex++)}
                        >
                            {group.label}
                        </Text>
                    )}
                    {group.items.map((item, i) => (
                        <div
                            key={i}
                            className={rowsClass || undefined}
                            style={rowStyle(rowIndex++)}
                        >
                            {item.to ? (
                                <LinkRow
                                    to={item.to}
                                    name={item.name}
                                    description={item.description}
                                    rightSection={item.rightSection}
                                    onClick={item.onClick}
                                />
                            ) : (
                                <LinkRow
                                    href={item.href ?? ""}
                                    download={item.download}
                                    name={item.name}
                                    description={item.description}
                                    rightSection={item.rightSection}
                                    onClick={item.onClick}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ))}
            {isSearching && filtered.length === 0 && (
                <Text className={classes.emptyText}>
                    {t("Phrase.NoSearchResults")}
                </Text>
            )}
            {hasMore && (
                <UnstyledButton
                    className={`${classes.expandButton} ${rowsClass}`.trim()}
                    style={rowStyle(rowIndex)}
                    onClick={() => setExpanded((e) => !e)}
                >
                    {expanded
                        ? t("common.showLess")
                        : t("common.showAll", {
                              count: filtered.length,
                              defaultValue: "Show all ({{count}})",
                          })}
                </UnstyledButton>
            )}
        </div>
    );
}
